import { openStore, shutdownStore, onStoreWriteError } from '@battleops/localstore';
import { createSync } from '@battleops/sync';
import { toRemote, toLogical } from '@battleops/shared';
import { platform } from '../platform';
import { loadIdentity, getDeviceId, transport } from './session';

// One store + one sync engine per signed-in identity. Safe to call from anywhere, any number
// of times — including the headless background-location task, which runs with no UI mounted.
let current = null;      // { key, store, sync }
let opening = null;
const statusListeners = new Set();
const registered = new Set();
const RESYNC_EPOCH = 1;
let lastStatus = { state: 'idle', pending: 0, failed: 0, lastSyncAt: null, lastError: null };

const keyOf = (id) => `${id.orgId}:${id.uid}`;

function publishStatus(st) { lastStatus = st; statusListeners.forEach((fn) => { try { fn(st); } catch {} }); }

// A tab that isn't (yet) the sync leader: the same engine API, but runs go to the leader over the channel
// and the status shown is the leader's. lead() = this tab just became the leader — act as the engine.
function followerSync(engine, relay) {
  let leading = false;
  relay.subscribe((m) => {
    if (leading) {
      if (m?.type === 'syncNow') engine.syncNow();
      else if (m?.type === 'hello') relay.post({ type: 'status', st: engine.status() });
      return;
    }
    if (m?.type === 'status') publishStatus(m.st);
  });
  relay.post({ type: 'hello' });
  const pass = (fn) => (...a) => (leading ? engine[fn](...a) : undefined);
  return {
    lead: () => { leading = true; },
    isLeader: () => leading,
    syncNow: () => (leading ? engine.syncNow() : relay.post({ type: 'syncNow' })),
    start: pass('start'), stop: () => engine.stop(), setOnline: pass('setOnline'), setForeground: pass('setForeground'),
    flush: pass('flush'), status: () => (leading ? engine.status() : lastStatus),
  };
}

export function onSyncStatus(fn) { statusListeners.add(fn); fn(lastStatus); return () => statusListeners.delete(fn); }
export const getSync = () => current?.sync || null;
/** The signed-in uid of the open store (for code that runs outside React, e.g. the live notice). */
export const currentUid = () => (current?.key ? current.key.split(':')[1] : null);

export async function ensureStore(identity) {
  const id = identity || (await loadIdentity());
  if (!id) return null;
  if (current && current.key === keyOf(id)) return current;
  if (opening) { await opening; if (current && current.key === keyOf(id)) return current; }

  opening = (async () => {
    await shutdown();
    const name = `battleops-${id.orgId}-${id.uid}`.replace(/[^A-Za-z0-9_-]/g, '_');
    const p = platform();
    hookReload(p);
    const driver = p.driver(name);
    const deviceId = await getDeviceId();
    // Web: several tabs share the database (docs/SYNC.md §2); only the leader tab runs the engine.
    const tabs = p.tabs ? p.tabs.coordinator(name) : null;
    const store = await openStore({
      driver, orgId: id.orgId, uid: id.uid, tabs: tabs || undefined,
      paths: { toRemote, toLogical },
      // Receipts waiting to upload stay on the device; the queue drains itself (services/receiptQueue.js).
      localOnly: ['receiptQueue', 'mediaQueue'],
    });
    // Bump to make every device re-snapshot once (repairs stores that missed changes). 1 = v0.1.128: stores that
    // pulled while the server didn't publish opsDepartments yet never got the departments made then.
    await store.resyncOnce(RESYNC_EPOCH).catch((e) => console.error('resync check failed', e));
    const relay = tabs ? p.tabs.channel(name) : null;
    const engine = createSync({
      store, transport, deviceId,
      onStatus: (st) => { publishStatus(st); relay?.post({ type: 'status', st }); },
    });
    const sync = relay ? followerSync(engine, relay) : engine;
    // A headless wake (location task, background sync) starts with no interval.
    // Start from the real network state so an offline open doesn't burn a failed run + retries.
    // (A tab that becomes the leader later reads the network when it takes over.)
    const startEngine = async () => {
      const up = await p.net.fetch().catch(() => null);
      if (up !== null) engine.setOnline(up);
      engine.start({ foreground: p.appState.current() === 'active' });
    };
    let stopLead = null;
    if (relay) stopLead = p.tabs.electLeader(name, () => { sync.lead(); startEngine(); });
    else await startEngine();
    current = { key: keyOf(id), store, sync, tabs, relay, stopLead };
    // Best effort, once per app start: lets the server know this device exists (reminder pokes later).
    if (!registered.has(current.key)) {
      registered.add(current.key);
      // With a push token (the phone's FCM, platform().pushToken) the server can wake it for an approval.
      Promise.resolve(p.pushToken ? p.pushToken() : null).catch(() => null)
        .then((pushToken) => transport.call('opsRegisterDevice', { deviceId, platform: p.os, appVersion: p.appVersion, runtimeVersion: p.runtimeVersion, ...(pushToken ? { pushToken } : {}) }))
        .catch(() => { registered.delete(keyOf(id)); });
    }
  })();
  try { await opening; } finally { opening = null; }
  return current;
}

/** Remove this identity's local store from the device (people.md P5: access ended). Shuts the store down first. */
export async function wipeLocal(identity) {
  const id = identity || (await loadIdentity());
  await shutdown();
  if (!id) return;
  const name = `battleops-${id.orgId}-${id.uid}`.replace(/[^A-Za-z0-9_-]/g, '_');
  try { const d = platform().driver(name); await d.init(); await d.wipe(); } catch (e) { console.error('wipe failed', e); }
}

export async function shutdown() {
  if (!current) return;
  const c = current;
  current = null;
  try { c.stopLead?.(); } catch {}
  try { c.sync.stop(); } catch {}
  try { c.relay?.close(); } catch {}
  try { await shutdownStore(); } catch {}
  try { c.tabs?.close(); } catch {}
}

// An update restart closes the database cleanly — after every other before-restart save. A restart
// that left a write open locked the database and every save after it failed until a force stop.
let reloadHooked = false;
function hookReload(p) {
  if (reloadHooked) return;
  reloadHooked = true;
  p.onBeforeReloadLast?.(() => shutdown());
}

// A local save failed. "locked"/"busy" = a stuck database: reopen it (listeners re-attach) and let
// the app know. Anything else, or a lock that comes straight back, is shown — never a silent revert.
const saveListeners = new Set();
let saveState = { state: 'ok', error: null, at: 0 };
const setSaveState = (s) => { saveState = s; saveListeners.forEach((fn) => { try { fn(s); } catch {} }); };
export function onSaveState(fn) { saveListeners.add(fn); fn(saveState); return () => saveListeners.delete(fn); }
let healing = false;
let lastHealAt = 0;
onStoreWriteError(async (err) => {
  const msg = String(err?.message || err);
  const locked = /locked|busy/i.test(msg);
  if (!locked || healing || Date.now() - lastHealAt < 60000) { setSaveState({ state: 'failing', error: msg, at: Date.now() }); return; }
  healing = true;
  lastHealAt = Date.now();
  setSaveState({ state: 'healing', error: msg, at: Date.now() });
  try {
    const id = await loadIdentity();
    await shutdown();
    if (id) await ensureStore(id);
    setSaveState({ state: 'ok', error: null, at: Date.now() });
  } catch (e) {
    setSaveState({ state: 'failing', error: String(e?.message || e), at: Date.now() });
  } finally { healing = false; }
});
/** A save went through again — clear the warning. */
export function markSaveOk() { if (saveState.state !== 'ok') setSaveState({ state: 'ok', error: null, at: Date.now() }); }
